import {Platform} from 'quasar';
import {Capacitor} from '@capacitor/core';
import {Directory, Filesystem} from '@capacitor/filesystem';
import axios from 'axios';
import {WebService} from './web.service';

const RE_CACHE_KEY_INVALID_CHARS = /[^a-zA-Z0-9]/g;

/**
 * matches any word containing "image", "Image", "img" or "Img"
 */
const RE_KEY_IMAGE = /\b\w*ima?ge?\w*\b/;

const RE_LOCAL_IMAGE_PATH = new RegExp('^/images/');

type TCacheKey = string; // alias for disambiguation

class DeviceFileCache {
	public static readonly CACHING_IS_ENABLED = Platform.is.capacitor;
	private static readonly SIMULTANEOUS_DOWNLOADS = 5;

	/**
	 * in-memory cache (dictionary) of local image file URLs by image key
	 *
	 * Because this is a JavaScript value, it will have to be repopulated
	 * on every app/page load. However, in most cases, it should find
	 * the images already on the filesystem from previous cache downloads.
	 *
	 * @private
	 */
	private cache: Record<TCacheKey, string | null> = {};

	/**
	 * unique list of item URLs to prefetch and add to cache
	 *
	 * @private
	 */
	private prefetchQueue: Set<string> = new Set();


	/**
	 * returns image from cache
	 *
	 * If the image doesn't exist yet in the cache and/or on the filesystem,
	 * it is retrieved and added. The value stored in and returned from the
	 * cache has already been processed through WebService.assetPath.
	 *
	 * N.B.: Values are cached by the given `url`. Stick with the values
	 *       returned from the server (i.e., do not use WebService.assetPath)
	 *       to promote consistency.
	 *
	 * @param url
	 */
	async get(url: string): Promise<string | null> {
		if(url === '') {
			return '';
		}
		if(url.endsWith('no-profile.jpg') ||
			WebService.isBundledImage(url)
		) {
			return url;
		}
		if(!this.cache[url]) {
			this.cache[url] = await this.findOrDownload(url);
		}
		return this.cache[url];
	}

	/**
	 * finds images within given object and adds them to caches (memory and filesystem)
	 *
	 * This returns a promise so that it is non-blocking. It doesn't return any value,
	 * because the caching is all handled within this class. In the calling code, simply
	 * add an empty `.then()` to satisfy linting.
	 *
	 * It could take a while to recurse through the object and fetch its images, especially
	 * on an initial load or when loading again after the cache has been cleared by the
	 * device. As a consequence, uncached images, especially those on the Home/start page,
	 * may visibly appear to load-in.
	 *
	 * @param crawlObject
	 */
	async precacheObjectImages<T = Record<string, unknown>>(crawlObject: T): Promise<void> {
		return new Promise(resolve => {
			if(DeviceFileCache.CACHING_IS_ENABLED) {
				// this synchronous/blocking call could take a while
				this.findObjectImagePaths<T>(crawlObject);

				const imagesToFetch = this.prefetchQueue.values();
				console.debug(`Precaching started for ${this.prefetchQueue.size} images.`);

				// TODO?: add images to queue "n" at a time
				for(let i = 0; i < this.prefetchQueue.size; i += DeviceFileCache.SIMULTANEOUS_DOWNLOADS) {
					this.get(imagesToFetch.next().value);
				}

				console.debug('Precaching completed.');
			}
			this.prefetchQueue.clear();
			resolve();
		});
	}

	private async blobToDataURL(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				resolve(reader.result as string);
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	/**
	 * converts an asset URL to a (hopefully) unique cache key
	 *
	 * Our images aren't distinguishable by path, alone. The filename is usually
	 * provided in a query param. We're more worried about avoiding collisions
	 * than making human-friendly cache keys, so use the whole path and query,
	 * stripped of any characters matching `RE_CACHE_KEY_INVALID_CHARS`.
	 *
	 * @param assetUrl
	 * @private
	 */
	private convertAssetUrlToKey(assetUrl: string): TCacheKey {
		const url = new URL(assetUrl);
		const cacheKey = `${url.pathname}${url.search}`;

		// strip all invalid characters and return
		return cacheKey?.replace(RE_CACHE_KEY_INVALID_CHARS, '') ?? '';
	}

	/**
	 * crawls given object for image properties to precache
	 *
	 * @param crawlObject
	 * @private
	 */
	private findObjectImagePaths<T = Record<string, unknown>>(crawlObject: T): void {
		const keys = Object.keys(crawlObject);

		keys.forEach(key => {
			// retrieve and cast value corresponding to key
			const value = crawlObject[key as keyof typeof crawlObject] as unknown;

			// recurse on objects and arrays
			if(typeof value === 'object' && value !== null) {
				if(Array.isArray(value)) {
					// object in an array
					value.forEach(val => {
						if(typeof val === 'object' && val !== null && !Array.isArray(val)) {
							this.findObjectImagePaths(val);
						}
						// TODO?: nested arrays
					});
				} else {
					// object in an object
					this.findObjectImagePaths(value);
				}
			} else {
				// recursion leaf
				if(RE_KEY_IMAGE.test(key) && typeof value === 'string') {
					this.prefetchQueue.add(value);
				}
			}
		});
	}

	/**
	 * returns the adjusted URL for the given `url`
	 *
	 * If caching is enabled, the returned URL will be for the local, cached
	 * file. If the file is local to the source code (e.g., from /public),
	 * the given `url` is returned. If no asset path can be determined, an
	 * empty string is returned. Otherwise, return the given `url` processed
	 * through WebService.assetPath.
	 *
	 * @param url
	 * @private
	 */
	private async findOrDownload(url: string): Promise<string | null> {
		/*
		 * Ignore images bundled with the app (i.e., from `(public)/images`).
		 *
		 * Because images under an '@' path, such as '@/assets', can only be used
		 * in the template, we don't need to worry about them here.
		 */
		if(RE_LOCAL_IMAGE_PATH.test(url)) {
			console.debug(`Cache ignoring local image ${url}.`);
			return new Promise(resolve => resolve(url));
		}

		const assetUrl = WebService.assetPath(url);
		if(assetUrl === null) {
			return new Promise(resolve => resolve(''));
		}

		if(DeviceFileCache.CACHING_IS_ENABLED) {
			const cachePath = this.convertAssetUrlToKey(assetUrl);

			try {
				// look for the file
				const file = await Filesystem.stat({
					path: cachePath,
					directory: Directory.Cache,
				});

				// convert device filepath into Web View-friendly path
				return Capacitor.convertFileSrc(file.uri);
			} catch(error) {
				/*
				 * If the file isn't found, `stat` will throw an error. Usually,
				 * that just means it hasn't been cached. In that case, retrieve
				 * the file from the server and cache it.
				 */
				console.debug(`Failed loading image from filesystem. Retrieving and caching ${assetUrl}.`);

				try {
					// download and store file
					const response = await axios.get(assetUrl, {
						responseType: 'blob',
					});
					const blob = response.data;
					const file = await Filesystem.writeFile({
						path: cachePath,
						data: await this.blobToDataURL(blob),
						directory: Directory.Cache,
						recursive: true,
					});
					return Capacitor.convertFileSrc(file.uri);
				} catch(e: unknown) {
					console.debug('Failed to fetch image from server', e);
					return null;
				}
			}
		}

		return assetUrl;
	}
}

export const DeviceFileCacheService = new DeviceFileCache();
